import * as amplitude from '@amplitude/analytics-browser';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { auth, db, functions as firebaseFunctions, storage } from '../firebase';

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');
const DEFAULT_CALLER_LABEL = 'Զանգահարող';
const DEFAULT_AGENT_NAME = 'AI Agent';
const MIN_TURN_DURATION_SECONDS = 0.6;
const DEFAULT_WORD_DURATION_SECONDS = 0.42;
const FALLBACK_SCAN_BATCH_SIZE = 50;
const MAX_FALLBACK_SCAN_ROUNDS = 20;
const listAgentConversationsCallable = httpsCallable(firebaseFunctions, 'listAgentConversations');

const normalizeConversationRole = (value) => {
  const normalized = trimString(value).toLowerCase();

  if (['caller', 'user', 'human', 'participant'].includes(normalized)) {
    return 'caller';
  }

  if (['agent', 'assistant', 'bot', 'ai'].includes(normalized)) {
    return 'agent';
  }

  return '';
};

const normalizeOptionalMs = (value) => {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 1e9 && value < 1e11) {
      return Math.max(0, Math.floor(value * 1000));
    }
    return Math.max(0, Math.floor(value));
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      if (numeric >= 1e9 && numeric < 1e11) {
        return Math.max(0, Math.floor(numeric * 1000));
      }
      return Math.max(0, Math.floor(numeric));
    }

    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const extractReportMessageText = (value) => {
  if (typeof value?.text_content === 'string' && trimString(value.text_content)) {
    return trimString(value.text_content);
  }

  if (typeof value?.text === 'string' && trimString(value.text)) {
    return trimString(value.text);
  }

  if (Array.isArray(value?.content)) {
    const joined = value.content
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object' && typeof item.text === 'string') {
          return item.text;
        }

        return '';
      })
      .filter(Boolean)
      .join('\n');

    return trimString(joined);
  }

  return '';
};

const resolveTurnSortTimeMs = (turn) => (
  normalizeOptionalMs(turn?.startedAtMs) ?? normalizeOptionalMs(turn?.endedAtMs)
);

const roundSeconds = (value) => Math.max(0, Math.round(Number(value || 0) * 1000) / 1000);

const estimateTurnDurationSeconds = (wordCount) => Math.max(
  MIN_TURN_DURATION_SECONDS,
  Math.max(1, wordCount) * DEFAULT_WORD_DURATION_SECONDS
);

const mapConversationListItem = (docSnap) => {
  const row = docSnap.data() || {};
  const createdAt = row.createdAt;

  return {
    id: docSnap.id,
    title: row.title || '',
    duration: row.duration || 0,
    source: row.source || 'upload',
    createdAtMs: createdAt && typeof createdAt.toMillis === 'function' ? createdAt.toMillis() : 0,
    status: row.status || '',
    starred: !!row.starred,
    metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : null,
  };
};

const isMatchingAgentConversation = (item, agentId) => (
  item.source === 'agent' && trimString(item.metadata?.agentId) === trimString(agentId)
);

const fetchAgentConversationsFallback = async ({ agentId, pageSize, cursorId }) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const recordingsRef = collection(db, 'recordings');
  const matchedItems = [];
  let scanCursorId = trimString(cursorId);
  let exhausted = false;
  let scanRounds = 0;

  while (!exhausted && matchedItems.length < pageSize + 1 && scanRounds < MAX_FALLBACK_SCAN_ROUNDS) {
    scanRounds += 1;

    let fallbackQuery = query(
      recordingsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(FALLBACK_SCAN_BATCH_SIZE)
    );

    if (scanCursorId) {
      const cursorSnap = await getDoc(doc(db, 'recordings', scanCursorId));
      if (cursorSnap.exists() && cursorSnap.data()?.userId === user.uid) {
        fallbackQuery = query(
          recordingsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          startAfter(cursorSnap),
          limit(FALLBACK_SCAN_BATCH_SIZE)
        );
      }
    }

    const snap = await getDocs(fallbackQuery);
    const docs = snap.docs;

    if (docs.length === 0) {
      exhausted = true;
      break;
    }

    for (const docSnap of docs) {
      scanCursorId = docSnap.id;

      if (matchedItems.length >= pageSize + 1) {
        break;
      }

      const item = mapConversationListItem(docSnap);
      if (isMatchingAgentConversation(item, agentId)) {
        matchedItems.push(item);
      }
    }

    exhausted = docs.length < FALLBACK_SCAN_BATCH_SIZE;
  }

  const items = matchedItems.slice(0, pageSize);

  return {
    items,
    nextCursorId: items.length > 0 ? items[items.length - 1].id : null,
    hasMore: matchedItems.length > pageSize,
  };
};

export const listAgentConversationsPage = async ({ agentId, pageSize = 25, cursorId = null }) => {
  try {
    const response = await listAgentConversationsCallable({
      agentId,
      pageSize,
      cursorId: cursorId || null,
    });
    const payload = response?.data && typeof response.data === 'object' ? response.data : {};

    return {
      items: Array.isArray(payload.items) ? payload.items : [],
      nextCursorId: trimString(payload.nextCursorId) || null,
      hasMore: Boolean(payload.hasMore),
    };
  } catch (callableError) {
    console.warn('Agent conversation callable unavailable, using direct Firestore fallback.', callableError);
    amplitude.track('Agent Conversation Fetch Fallback Used', {
      agentId: trimString(agentId),
      cursorId: trimString(cursorId),
      error: callableError?.message || String(callableError),
    });

    return fetchAgentConversationsFallback({ agentId, pageSize, cursorId });
  }
};

export const getSignedAgentConversationAudioUrl = async ({ conversationId }) => {
  const getRecordingDownloadUrl = httpsCallable(firebaseFunctions, 'getRecordingDownloadUrl');
  const response = await getRecordingDownloadUrl({ docId: conversationId });
  return trimString(response?.data?.url);
};

export const updateAgentConversation = async ({ conversationId, updates }) => {
  if (!conversationId) {
    throw new Error('Conversation not found.');
  }

  await updateDoc(doc(db, 'recordings', conversationId), updates);
};

export const deleteAgentConversation = async ({ conversationId, conversation }) => {
  if (!conversationId) {
    throw new Error('Conversation not found.');
  }

  const filePath = trimString(conversation?.filePath);
  const sessionReportPath = trimString(conversation?.metadata?.sessionReportPath);

  if (filePath) {
    await deleteObject(ref(storage, filePath));
  }

  if (sessionReportPath) {
    try {
      await deleteObject(ref(storage, sessionReportPath));
    } catch (error) {
      console.warn('Error deleting agent session report:', error);
    }
  }

  await deleteDoc(doc(db, 'recordings', conversationId));
};

export const formatConversationDate = (value) => {
  if (!value) {
    return '—';
  }

  const dateValue = typeof value?.toDate === 'function'
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(dateValue.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('hy-AM', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dateValue);
};

export const getConversationCallerLabel = (conversation) => (
  trimString(conversation?.metadata?.callerNumber) || DEFAULT_CALLER_LABEL
);

export const normalizeAgentConversationTurns = (turns = []) => {
  if (!Array.isArray(turns)) {
    return [];
  }

  const normalizedTurns = turns.reduce((result, entry, index) => {
    const role = normalizeConversationRole(entry?.role);
    const text = trimString(entry?.text || entry?.content);

    if (!role || !text) {
      return result;
    }

    const startedAtMs = normalizeOptionalMs(entry?.startedAtMs ?? entry?.started_at_ms);
    const endedAtMs = normalizeOptionalMs(entry?.endedAtMs ?? entry?.ended_at_ms);
    const normalizedTurn = {
      role,
      text,
      originalIndex: index,
    };

    if (startedAtMs != null) {
      normalizedTurn.startedAtMs = startedAtMs;
    }

    if (endedAtMs != null) {
      normalizedTurn.endedAtMs = endedAtMs;
    }

    result.push(normalizedTurn);
    return result;
  }, []);

  normalizedTurns.sort((left, right) => {
    const leftTime = resolveTurnSortTimeMs(left);
    const rightTime = resolveTurnSortTimeMs(right);
    const leftHasTime = leftTime != null;
    const rightHasTime = rightTime != null;

    if (leftHasTime && rightHasTime && leftTime !== rightTime) {
      return leftTime - rightTime;
    }

    if (leftHasTime !== rightHasTime) {
      return leftHasTime ? -1 : 1;
    }

    return left.originalIndex - right.originalIndex;
  });

  return normalizedTurns.reduce((result, entry) => {
    const { originalIndex, ...turn } = entry;
    const previous = result[result.length - 1];

    if (previous && previous.role === turn.role && previous.text === turn.text) {
      return result;
    }

    result.push(turn);
    return result;
  }, []);
};

export const extractAgentConversationTurnsFromSessionReport = (sessionReport) => {
  if (!sessionReport || typeof sessionReport !== 'object') {
    return [];
  }

  const eventTurns = Array.isArray(sessionReport.events)
    ? sessionReport.events.reduce((result, event, index) => {
      if (event?.type !== 'conversation_item_added') {
        return result;
      }

      const item = event?.item && typeof event.item === 'object' ? event.item : null;
      const role = normalizeConversationRole(item?.role);
      const text = extractReportMessageText(item);

      if (!role || !text) {
        return result;
      }

      const startedAtMs = normalizeOptionalMs(item?.created_at ?? event?.created_at);
      const turn = { role, text, originalIndex: index };
      if (startedAtMs != null) {
        turn.startedAtMs = startedAtMs;
      }

      result.push(turn);
      return result;
    }, [])
    : [];

  if (eventTurns.length > 0) {
    return normalizeAgentConversationTurns(eventTurns);
  }

  const historyItems = Array.isArray(sessionReport?.chat_history?.items)
    ? sessionReport.chat_history.items
    : [];

  return normalizeAgentConversationTurns(historyItems.map((item, index) => {
    const role = normalizeConversationRole(item?.role);
    const text = extractReportMessageText(item);
    const startedAtMs = normalizeOptionalMs(item?.created_at);

    return {
      role,
      text,
      originalIndex: index,
      ...(startedAtMs != null ? { startedAtMs } : {}),
    };
  }));
};

export const buildAgentConversationTranscript = (
  turns = [],
  agentName = DEFAULT_AGENT_NAME,
  callerLabel = DEFAULT_CALLER_LABEL
) => {
  const normalizedTurns = normalizeAgentConversationTurns(turns);
  const normalizedAgentName = trimString(agentName) || DEFAULT_AGENT_NAME;
  const normalizedCallerLabel = trimString(callerLabel) || DEFAULT_CALLER_LABEL;

  return normalizedTurns
    .map((turn) => `${turn.role === 'agent' ? normalizedAgentName : normalizedCallerLabel}: ${turn.text}`)
    .join('\n');
};

export const buildAgentConversationTimelineTurns = ({
  turns = [],
  sessionReport = null,
  agentName = DEFAULT_AGENT_NAME,
  callerLabel = DEFAULT_CALLER_LABEL,
}) => {
  const normalizedTurns = extractAgentConversationTurnsFromSessionReport(sessionReport).length > 0
    ? extractAgentConversationTurnsFromSessionReport(sessionReport)
    : normalizeAgentConversationTurns(turns);
  if (!normalizedTurns.length) {
    return [];
  }

  const normalizedAgentName = trimString(agentName) || DEFAULT_AGENT_NAME;
  const normalizedCallerLabel = trimString(callerLabel) || DEFAULT_CALLER_LABEL;
  const explicitTimes = normalizedTurns
    .flatMap((turn) => [normalizeOptionalMs(turn.startedAtMs), normalizeOptionalMs(turn.endedAtMs)])
    .filter((value) => value != null);
  const baseTimeMs = explicitTimes.length ? Math.min(...explicitTimes) : null;

  let cursorSeconds = 0;

  return normalizedTurns.map((turn) => {
    const words = trimString(turn.text).split(/\s+/).filter(Boolean);
    const startedAtMs = normalizeOptionalMs(turn.startedAtMs);
    const endedAtMs = normalizeOptionalMs(turn.endedAtMs);
    const explicitStartSeconds = baseTimeMs != null && startedAtMs != null
      ? Math.max(0, (startedAtMs - baseTimeMs) / 1000)
      : null;
    const explicitEndSeconds = baseTimeMs != null && endedAtMs != null
      ? Math.max(0, (endedAtMs - baseTimeMs) / 1000)
      : null;
    const startSeconds = explicitStartSeconds != null
      ? Math.max(cursorSeconds, explicitStartSeconds)
      : cursorSeconds;
    const endSeconds = explicitEndSeconds != null
      ? Math.max(startSeconds + MIN_TURN_DURATION_SECONDS, explicitEndSeconds)
      : startSeconds + estimateTurnDurationSeconds(words.length);

    cursorSeconds = endSeconds;

    return {
      ...turn,
      speakerLabel: turn.role === 'agent' ? normalizedAgentName : normalizedCallerLabel,
      startSeconds: roundSeconds(startSeconds),
      endSeconds: roundSeconds(endSeconds),
    };
  });
};

export const getAgentSummarySections = (conversation) => {
  const summary = conversation?.agentSummary && typeof conversation.agentSummary === 'object'
    ? conversation.agentSummary
    : {};

  return {
    intent: trimString(summary.intent),
    overview: trimString(summary.overview),
    outcome: trimString(summary.outcome),
    actionItems: Array.isArray(summary.actionItems)
      ? summary.actionItems.map((item) => trimString(item)).filter(Boolean)
      : [],
  };
};
